import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import aboutImg from "../assets/about-amresh-prajapati-web-developer-mumbai.webp";
import profileImg from "../assets/amresh-prajapati-web-developer-profile.png";
import webDevImg from "../assets/amresh-prajapati-freelance-website-developer-mumbai.webp";
import {
  FaArrowRight,
  FaCode,
  FaRocket,
  FaLaptopCode,
  FaWordpress,
  FaCogs,
  FaSearch,
  FaPalette,
  FaGlobe,
  FaCheckCircle,
} from 'react-icons/fa';
import './About.css';

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: { opacity: 1, y: 0 },
};

const stats = [
  { value: '4+', label: 'Years Experience' },
  { value: '70+', label: 'Projects Completed' },
  { value: '20+', label: 'Happy Clients' },
  { value: '5+', label: 'Countries Served' },
];

const skills = [
  { icon: <FaLaptopCode />, title: 'Custom Web Development', desc: 'Fast, responsive and production-ready websites using React JS, Node.js, Express and PHP.' },
  { icon: <FaWordpress />, title: 'WordPress Websites', desc: 'Business websites, blogs, landing pages and custom WordPress solutions.' },
  { icon: <FaCogs />, title: 'Custom CRM & ERP Solutions', desc: 'CRM and ERP software with dashboards, automation and scalable workflows.' },
  { icon: <FaSearch />, title: 'SEO Optimization', desc: 'Fast, structured and SEO-ready websites for better Google visibility.' },
  { icon: <FaPalette />, title: 'UI/UX Design', desc: 'Premium designs with strong layout, colors, spacing and conversion focus.' },
];

const journey = [
  {
    year: '2022',
    title: 'Started Freelancing',
    desc: 'Started my journey as a freelance web developer, building websites for small businesses and personal brands.'
  },
  {
    year: '2023',
    title: 'Skill Expansion',
    desc: 'Worked on multiple projects and improved skills in React, WordPress, and modern frontend development.'
  },
  {
    year: '2024',
    title: 'Client Growth',
    desc: 'Delivered websites for clients across different industries with focus on UI, performance and responsiveness.'
  },
  {
    year: '2025',
    title: 'Full Stack Development',
    desc: 'Expanded into backend development and started building complete web solutions including APIs and databases.'
  },
  {
    year: 'Present',
    title: 'Full Stack + CRM Solutions 🚀',
    desc: 'Building complete digital systems including frontend, backend and custom CRM software with dashboards, automation and scalable architecture.'
  }
];

const values = [
  'Clean & professional design',
  'Responsive on all devices',
  'Fast loading website',
  'SEO-friendly structure',
  'Transparent communication',
  'Long-term support',
];

export default function About() {
  return (
    <>
      <Helmet>
        <title>About Amresh Prajapati | Freelance Web Developer Mumbai</title>
        <meta
          name="description"
          content="Meet Amresh Prajapati — freelance web developer in Mumbai with 4+ years experience and 70+ completed projects. Skilled in React JS, Node.js, PHP, MySQL, custom CRM & ERP solutions, WordPress, SEO & UI/UX design."
        />
        <meta
          name="keywords"
          content="about Amresh Prajapati, freelance web developer Mumbai, website developer profile, React developer Mumbai, WordPress expert Mumbai, SEO specialist Mumbai, web designer Virar Vasai"
        />
        <link rel="canonical" href="https://freelanceramreshprajapatimumbai.in/about" />
      </Helmet>

      <section className="aboutHero">
        <div className="aboutHero__glow aboutHero__glow--one" />
        <div className="aboutHero__glow aboutHero__glow--two" />

        <div className="aboutContainer aboutHero__grid">
          <motion.div
            className="aboutHero__content"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.7 }}
          >
            <span className="aboutTag">
              <FaGlobe /> About Me
            </span>

            <h1>
              I Build Premium Websites That Help Brands <span>Grow Online</span>
            </h1>

            <p>
              Hi, I’m <strong>Amresh Prajapati</strong>, a freelance website developer and
              designer from Virar West, Mumbai. I build fast, responsive and SEO-ready
              websites using React JS, Node.js, PHP and MySQL — along with custom CRM and
              ERP solutions for businesses, startups, institutes and growing brands.
            </p>

            <div className="aboutHero__buttons">
              <Link to="/contact" className="aboutBtn aboutBtn--primary">
                Hire Me <FaArrowRight />
              </Link>
              <Link to="/projects" className="aboutBtn aboutBtn--outline">
                View Projects
              </Link>
            </div>
          </motion.div>

          <motion.div
            className="aboutHero__visual"
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="aboutHero__imageCard">
             <img
  src={aboutImg}
  alt="Premium web development workspace"
/>

<div className="aboutHero__profile">
  <img
    src={profileImg}
    alt="Amresh Prajapati"
  />
                <div>
                  <strong>Amresh Prajapati</strong>
                  <span>Website Developer</span>
                </div>
              </div>

              <div className="aboutHero__floating">
                <FaRocket />
                <span>Modern Web Solutions</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="aboutStats">
        <div className="aboutContainer aboutStats__grid">
          {stats.map((item, index) => (
            <motion.div
              className="aboutStat"
              key={item.label}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="aboutSection">
        <div className="aboutContainer aboutStory">
          <motion.div
            className="aboutStory__image"
            initial={{ opacity: 0, x: -45 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <img
  src={webDevImg}
  alt="Website strategy and development"
/>
            <div className="aboutStory__badge">
              <FaCode />
              <span>Full Stack Developer + SEO</span>
            </div>
          </motion.div>

          <motion.div
            className="aboutStory__content"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="aboutTag">
              <FaCode /> My Story
            </span>

            <h2>
              From Simple Websites To <span>Complete Digital Experiences</span>
            </h2>

            <p>
              I started my journey with a clear goal — helping businesses build a powerful
              online presence. Today, I deliver complete digital solutions including website
              design, full-stack development with React JS and Node.js, custom CRM &amp; ERP
              software, SEO setup, speed optimization and responsive user experience.
            </p>

            <p>
              My focus is not just making a website look good. I build websites that load fast,
              feel premium, work smoothly on mobile and help users take action.
            </p>

            <div className="aboutChecklist">
              {values.map((item) => (
                <div className="aboutChecklist__item" key={item}>
                  <FaCheckCircle />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="aboutSection aboutSection--dark">
        <div className="aboutContainer">
          <motion.div
            className="aboutSectionHead"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="aboutTag">
              <FaRocket /> What I Do
            </span>
            <h2>
              Services That Make Your Website <span>Stand Out</span>
            </h2>
            <p>
              I combine design, development and SEO to create websites that look beautiful and perform well.
            </p>
          </motion.div>

          <div className="aboutSkills">
            {skills.map((skill, index) => (
              <motion.div
                className="aboutSkillCard"
                key={skill.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <div className="aboutSkillCard__icon">{skill.icon}</div>
                <h3>{skill.title}</h3>
                <p>{skill.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="aboutSection">
        <div className="aboutContainer">
          <motion.div
            className="aboutSectionHead"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="aboutTag">
              <FaRocket /> Journey
            </span>
            <h2>
              My Growth <span>Timeline</span>
            </h2>
          </motion.div>

          <div className="aboutTimeline">
            {journey.map((item, index) => (
              <motion.div
                className="aboutTimeline__item"
                key={item.year}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <div className="aboutTimeline__dot" />
                <div className="aboutTimeline__card">
                  <span>{item.year}</span>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="aboutCTA">
        <div className="aboutContainer">
          <motion.div
            className="aboutCTA__box"
            initial={{ opacity: 0, y: 35 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="aboutTag">
              <FaRocket /> Start Your Project
            </span>
            <h2>
              Have A Website Idea? Let’s Build It <span>Together</span>
            </h2>
            <p>
              I can help you create a modern business website, portfolio, landing page,
              WordPress site, e-commerce store or custom CRM / ERP solution.
            </p>

            <div className="aboutHero__buttons">
              <Link to="/contact" className="aboutBtn aboutBtn--primary">
                Get In Touch <FaArrowRight />
              </Link>
              <a href="mailto:info@freelanceramreshprajapatimumbai.in" className="aboutBtn aboutBtn--outline">
                Email Me
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}